import { type ReportableScale, type ReportData, type ReportSelectorData, YAxisScaleMap } from "@qendo/profile/reports";
import { useMemo } from "react";
import { View } from "react-native";
import { Button } from "~/components/Button";
import { Badge } from "~/components/feedback/Badge";
import { ComboBox, type ItemType } from "~/components/form/combo-box/ComboBox";
import { ReportAverage } from "~/components/layout/reports/ReportAverage";
import { BaseText } from "~/components/layout/text";
import { QendoIcon } from "~/components/QendoIcon";
import { Flex, Gap, Items } from "~/constants/layout";
import { Space } from "~/constants/sizing";
import { useReport } from "~/providers/ReportProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { filled, nullish } from "~/util";
import { filterUnique } from "~/util/array";
import { extractColours } from "~/util/qendo/reports";
import type { OptionalNumeric } from "~/util/string";

export type ComparisonSelectorProps = {
    report: ReportSelectorData;
    selected: string[];
    data?: ReportData | null;
    onChangeSelected: (value: string[]) => void;
};

type ReportKey = {
    key: string;
    colour: string;
    name: string;
    average: OptionalNumeric;
    scale: ReportableScale;
    scale_name: string;
};

export const ComparisonSelector = ({ report, selected, data, onChangeSelected }: ComparisonSelectorProps) => {
    const { colours } = useTheme();
    const { reports } = useReport();

    const legend: ReportKey[] = useMemo(() => {
        const cols = extractColours(colours, data?.compare);
        return [
            {
                key: report.trackable_id,
                colour: cols[0],
                name: report.title,
                average: data?.average ?? report.average,
                scale: report.scale,
                scale_name: report.scale_name,
            },
            ...(data?.compare ?? []).map(({ trackable_id, average }, idx) => {
                const trackable = reports.find((r) => r.trackable_id === trackable_id) as ReportSelectorData;

                return {
                    key: trackable_id,
                    colour: cols[idx + 1],
                    name: trackable.title,
                    average: average,
                    scale: trackable.scale,
                    scale_name: trackable.scale_name,
                };
            }),
        ];
    }, [colours, data, report, reports]);

    const main_scale = YAxisScaleMap[report.scale];
    const secondary_scale = useMemo(
        () =>
            selected
                .map((id) => reports.find((r) => r.trackable_id === id)?.scale)
                .find((scale) => YAxisScaleMap[scale as ReportableScale] !== main_scale),
        [selected, reports, main_scale],
    );

    const except = useMemo(() => [report.trackable_id, ...selected], [report.trackable_id, selected]);

    const items: ItemType<string>[] = useMemo(
        () =>
            reports
                .filter(
                    ({ trackable_id, scale }) =>
                        !except.includes(trackable_id) &&
                        (!secondary_scale || [main_scale, secondary_scale].includes(YAxisScaleMap[scale])),
                )
                .map(({ icon, title, trackable_id }) => ({
                    value: trackable_id,
                    label: title,
                    icon: () => <QendoIcon name={icon} size={16} variant="secondary" />,
                })),
        [reports, except, secondary_scale, main_scale],
    );

    const addItem = (val: string | null) => {
        if (val) {
            onChangeSelected([...selected, val].filter(filterUnique));
        }
    };

    const removeItem = (val: string) => {
        onChangeSelected(selected.filter((v) => v !== val));
    };
    return (
        <View style={Gap.lg}>
            {filled(items) && (
                <ComboBox<string, ItemType<string>>
                    label={`Compare ${report.title.toLowerCase()} to`}
                    onItemSelected={addItem}
                    items={items}
                    addable={false}
                    disabled={items.length === 0 || selected.length >= 4}
                />
            )}
            <View style={Gap.sm}>
                {legend.map(({ key, name, colour, average, scale, scale_name }) => (
                    <View style={[Flex.row, Gap.sm, Flex.start, Items.center]} key={key}>
                        <QendoIcon name="circle-lg-filled" color={colour} />
                        <View style={[Flex.row, Gap.sm, Flex.start, Items.center, Flex.grow]}>
                            <BaseText>{name}</BaseText>
                            <Badge
                                variant="secondary"
                                size="sm"
                                title={
                                    nullish(average) ? (
                                        "no data in this timeframe"
                                    ) : (
                                        <ReportAverage
                                            scale={scale}
                                            scale_name={scale_name}
                                            average={average}
                                            averageProps={{ size: "sm" }}
                                            subtitleProps={{ style: { paddingLeft: Space.xs } }}
                                        />
                                    )
                                }
                            />
                        </View>
                        {key === report.trackable_id || (
                            <Button variant="neutral-quiet" icon="xmark" onPress={() => removeItem(key)} />
                        )}
                    </View>
                ))}
            </View>
        </View>
    );
};
